/**
 * SJ product template helpers (templates/product.sjproduct.json)
 * - Bundle plan cards: keep the product form quantity + button price in sync with the chosen pack
 * - Video stories / video cards: open the clip in a lightbox
 * - Horizontal scrollers: previous / next arrows
 * Everything uses event delegation so it keeps working after Horizon re-renders the section.
 */
if (!window.__sjProductReady) {
  window.__sjProductReady = true;

  /* ---------- Bundle plan ---------- */

  /** @param {Element} bundle */
  const bundleScope = (bundle) =>
    bundle.closest('product-component') || bundle.closest('.shopify-section') || document;

  /** @param {Element} bundle */
  const applyBundle = (bundle) => {
    const checked = bundle.querySelector('input[type="radio"]:checked');
    if (!(checked instanceof HTMLInputElement)) return;
    const scope = bundleScope(bundle);
    const quantity = checked.value || '1';

    bundle.querySelectorAll('.sjbundle__tier').forEach((tier) => {
      tier.classList.toggle('is-selected', tier.contains(checked));
    });

    const selector = scope.querySelector('quantity-selector-component:not([data-cart-line])');
    const form = scope.querySelector('form[data-type="add-to-cart-form"]');
    let input = scope.querySelector('quantity-selector-component input[name="quantity"]');
    if (!input && form) {
      input = form.querySelector('input[name="quantity"]');
      if (!input) {
        input = document.createElement('input');
        input.setAttribute('type', 'hidden');
        input.setAttribute('name', 'quantity');
        form.appendChild(input);
      }
    }
    if (selector && typeof (/** @type {any} */ (selector).setValue) === 'function') {
      /** @type {any} */ (selector).setValue(quantity);
    } else if (input instanceof HTMLInputElement) {
      input.value = quantity;
    }

    // "ADD TO CART - Rs. 1,098" -> price of the selected pack
    const priceLabel = checked.dataset.priceLabel;
    if (!priceLabel) return;
    scope.querySelectorAll('.buy-buttons-block button[name="add"]').forEach((button) => {
      if (button.hasAttribute('disabled')) return;
      const textNode = Array.from(button.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE && (node.textContent || '').includes(' - ')
      );
      if (!textNode) return;
      const base = (textNode.textContent || '').split(' - ')[0];
      textNode.textContent = `${base} - ${priceLabel}`;
    });
  };

  const applyAllBundles = () => document.querySelectorAll('[data-sj-bundle]').forEach(applyBundle);

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const bundle = target.closest('[data-sj-bundle]');
    if (bundle) {
      applyBundle(bundle);
      return;
    }
    // Variant changed: Horizon swaps in fresh markup shortly after, so re-sync then
    if (target.closest('variant-picker')) {
      setTimeout(applyAllBundles, 700);
      setTimeout(applyAllBundles, 1800);
    }
  });

  // Make sure the quantity is right at the moment of adding to cart / buying now
  const syncBeforeBuy = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('.buy-buttons-block, sticky-add-to-cart')) return;
    applyAllBundles();
  };
  document.addEventListener('click', syncBeforeBuy, true);
  document.addEventListener('submit', syncBeforeBuy, true);

  applyAllBundles();
  document.addEventListener('DOMContentLoaded', applyAllBundles);
  document.addEventListener('shopify:section:load', applyAllBundles);

  /* ---------- Video lightbox ---------- */

  /** @type {HTMLDialogElement | null} */
  let dialog = null;

  const closeDialog = () => {
    if (!dialog) return;
    const video = dialog.querySelector('video');
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
    if (dialog.open) dialog.close();
  };

  const getDialog = () => {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'sjvideo-dialog';
    dialog.innerHTML =
      '<button type="button" class="sjvideo-dialog__close" aria-label="Close">&times;</button>' +
      '<video class="sjvideo-dialog__video" controls playsinline></video>';
    document.body.appendChild(dialog);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || /** @type {Element} */ (event.target).closest('.sjvideo-dialog__close')) {
        closeDialog();
      }
    });
    dialog.addEventListener('close', closeDialog);
    return dialog;
  };

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const opener = target.closest('[data-sj-video]');
    if (opener instanceof HTMLElement && opener.dataset.sjVideo) {
      event.preventDefault();
      const box = getDialog();
      const video = /** @type {HTMLVideoElement} */ (box.querySelector('video'));
      video.src = opener.dataset.sjVideo;
      if (opener.dataset.sjPoster) video.poster = opener.dataset.sjPoster;
      else video.removeAttribute('poster');
      box.showModal();
      video.play().catch(() => {});
      return;
    }

    /* ---------- Scroller arrows ---------- */
    const arrow = target.closest('[data-sj-scroll]');
    if (arrow instanceof HTMLElement) {
      const wrap = arrow.closest('[data-sj-scroller]');
      const track = wrap?.querySelector('[data-sj-track]');
      if (!track) return;
      const direction = arrow.dataset.sjScroll === 'prev' ? -1 : 1;
      const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
      track.scrollBy({ left: direction * rtl * track.clientWidth * 0.85, behavior: 'smooth' });
    }
  });

  /* ---------- Hide scroller arrows when everything already fits ---------- */
  const measureScrollers = () => {
    document.querySelectorAll('[data-sj-scroller]').forEach((wrap) => {
      const track = wrap.querySelector('[data-sj-track]');
      if (!track) return;
      wrap.classList.toggle('sjscroll--fits', track.scrollWidth <= track.clientWidth + 2);
    });
  };
  measureScrollers();
  window.addEventListener('load', measureScrollers);
  window.addEventListener('resize', measureScrollers);
  document.addEventListener('shopify:section:load', measureScrollers);
}
